import React, { useState } from 'react';
import InputField from '../common/InputField';
import Button from '../common/Button';
import PropTypes from 'prop-types';
import Logo from '../common/Logo';
import { Eye, EyeOff } from 'lucide-react';
import AuthService from '../../services/auth.service';

const RegistrationForm = ({ 
  onSwitchForm, 
  preselectedTagId,
  onFieldChange,
  onResetError,
  
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    password: '',
    phone: '',
    gender: '',
    tag_id: preselectedTagId || '',
    city: '',
    privacy_accepted: false 
  });

  // Stati per la gestione del form
  const [registrationPhase, setRegistrationPhase] = useState('initial');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const tags = [
    { id: 1, name: "Ansia e depressione" },
    { id: 3, name: "Stress e burnout" },
    { id: 4, name: "Crescita personale" },
    { id: 5, name: "Relazioni, famiglia, coppie" },
    { id: 6, name: "Trauma e abusi" },
    { id: 7, name: "Logopedia e DSA" },
    { id: 8, name: "Intimità e benessere sessuale" },
    { id: 14, name: "Altre aree di difficoltà" },
    { id: 15, name: "Nessuno di questi percorsi" }
  ];

  const handleChange = (e) => {
    if (onFieldChange) {
      onFieldChange();
    }
    
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleInitialSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacy_accepted) {
      setError('È necessario accettare la Privacy Policy per continuare');
      return;
    }
    
    try {
        setError('');
        
        // Prima verifichiamo i duplicati
        const checkResult = await AuthService.checkDuplicates(
            formData.email,
            formData.phone
        );

        // Se ci sono duplicati, mostra errore e ferma il processo
        if (!checkResult.success) {
            const errorMessages = Object.values(checkResult.errors);
            setError(errorMessages.join('. '));
            return;
        }

        // Se non ci sono duplicati, procedi con l'invio OTP
        const response = await AuthService.sendRegistrationOtp(formData.phone);
        if (response.success) {
            setRegistrationPhase('verification');
        }
    } catch (error) {
        setError(error.message);
    }
};

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    try {
        setError('');
        const response = await AuthService.completeRegistration({
            ...formData,
            otp: otpCode
        });
        
        if (response.success) {
            setSuccess(true);
            setTimeout(() => {
                onSwitchForm('login');
            }, 4000);
        } else {
            setError(response.error || response.message || 'Errore durante la verifica');
        }
    } catch (error) {
        setError(error.message || 'Codice OTP non valido');
    }
};

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Form di verifica OTP
  if (registrationPhase === 'verification') {
    return (
      <form onSubmit={handleOtpSubmit} className="bg-white p-8 rounded-lg w-full max-w-md">
        <Logo />
        <h2 className="text-2xl font-bold mb-2 text-center">Verifica il tuo numero</h2>
        <p className="text-gray-600 mb-6">
          Abbiamo inviato un codice di verifica al numero {formData.phone}.
          <br />
          Riceverai il messaggio entro 60 secondi.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
            Registrazione completata con successo! Verrai reindirizzato al login.
          </div>
        )}

        <InputField
          type="text"
          placeholder="Inserisci il codice ricevuto via SMS"
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value)}
          maxLength={6}
          className="mb-4 w-full border border-orange-300 rounded-md p-2"
          required
        />

        <Button
          type="submit"
          text="Verifica e Completa Registrazione"
          className="w-full bg-orange-500 text-white py-3 rounded-md hover:bg-orange-600 mb-6"
        />

        <Button
          type="button" // Importante: type="button" per evitare il submit del form
          text="Torna alla registrazione"
          onClick={() => setRegistrationPhase('initial')}
          className="w-full bg-gray-100 text-gray-700 py-3 rounded-md hover:bg-gray-200 border border-gray-300"
        />

      </form>
    );
  }

  // Form iniziale di registrazione
  return (
    <form onSubmit={handleInitialSubmit} className="bg-white p-8 rounded-lg w-full max-w-md">
      <Logo />
      <h2 className="text-2xl font-bold mb-2 text-center">Crea un account</h2>
      <p className="text-gray-600 mb-6">Inizia il tuo percorso con Personal Therapy</p>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <InputField
        type="text"
        name="nome"
        placeholder="Nome"
        className="mb-4 w-full border border-orange-300 rounded-md p-2"
        value={formData.nome}
        onChange={handleChange}
        required
      />

      <InputField
        type="text"
        name="cognome"
        placeholder="Cognome"
        className="mb-4 w-full border border-orange-300 rounded-md p-2"
        value={formData.cognome}
        onChange={handleChange}
        required
      />

      <InputField
        type="text"
        name="city"
        placeholder="Città di residenza"
        className="mb-4 w-full border border-orange-300 rounded-md p-2"
        value={formData.city}
        onChange={handleChange}
      />

      <InputField
        type="email"
        name="email"
        placeholder="Email"
        className="mb-4 w-full border border-orange-300 rounded-md p-2"
        value={formData.email}
        onChange={handleChange}
        required
      />

      <div className="relative mb-4">
        <InputField
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="Password"
          className="w-full border border-orange-300 rounded-md p-2 pr-10"
          value={formData.password}
          onChange={handleChange}
          required
        />
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          onClick={togglePasswordVisibility}
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      </div>

      <InputField
        type="tel"
        name="phone"
        placeholder="Telefono"
        className="mb-4 w-full border border-orange-300 rounded-md p-2"
        value={formData.phone}
        onChange={handleChange}
        required
      />
      
      <select
        name="gender"
        className="mb-6 w-full border border-orange-300 rounded-md p-2"
        value={formData.gender}
        onChange={handleChange}
        required
      >
        <option value="">Seleziona il tuo genere</option>
        <option value="M">Maschio</option>
        <option value="F">Femmina</option>
        <option value="OTHER">Non binario</option>
      </select>

      <select
        name="tag_id"
        className="mb-6 w-full border border-orange-300 rounded-md p-2"
        value={formData.tag_id}
        onChange={handleChange}
        required
      >
        <option value="">Seleziona il tuo percorso</option>
        {tags.map(tag => (
          <option 
            key={tag.id}
            value={tag.id}
            selected={tag.id === parseInt(preselectedTagId)}
          >
            {tag.name}
          </option>
        ))}
      </select>

      {/* Checkbox per la Privacy Policy */}
      <div className="mb-6 flex items-start">
        <input
          type="checkbox"
          id="privacy_accepted"
          name="privacy_accepted"
          checked={formData.privacy_accepted}
          onChange={handleChange}
          className="mt-1 mr-2 h-4 w-4 accent-orange-500"
          required
        />
        <label htmlFor="privacy_accepted" className="text-gray-700 text-sm">
          Ho preso visione della <a 
            href="/assets/pdf/privacy-policy.pdf" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-orange-500 hover:underline"
          >
            Privacy Policy
          </a> *
        </label>
      </div>

      <Button
        type="submit"
        text="Registrati"
        className="w-full bg-orange-500 text-white py-3 rounded-md hover:bg-orange-600 mb-6"
      />

      <Button
        type="button"
        text="Reset Campi"
        onClick={() => {
          setFormData({
            nome: '',
            cognome: '',
            email: '',
            password: '',
            phone: '',
            gender: 'OTHER',
            tag_id: preselectedTagId || '',
            city: '',
            privacy_accepted: false 
          });
          if (onResetError) {
            onResetError();
          }
        }}
        className="w-full text-gray-400 py-3 rounded-md hover:text-gray-500 border-none mb-6"
      />
      
      <div className="text-center">
        <span className="text-gray-600">Hai già un account? </span>
        <button
          type="button"
          onClick={() => onSwitchForm('login')}
          className="text-orange-500 hover:underline"
        >
          Accedi
        </button>
      </div>
    </form>
  );
};

RegistrationForm.propTypes = {
  onSwitchForm: PropTypes.func.isRequired,
  preselectedTagId: PropTypes.string,
  onFieldChange: PropTypes.func,    
  onResetError: PropTypes.func,  
};

export default RegistrationForm;